import { useMemo, useState } from 'react';
import { formatDuration, formatDurationShort } from '../lib/format.js';

function shortDay(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short' });
}

export default function StatsBar({ stats, runningExtraSeconds = 0, runningOnOwnedTask = false, onTaskOpen }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('chart');

  const personalToday = useMemo(() => {
    if (!stats) return 0;
    return stats.personal.todaySeconds + runningExtraSeconds;
  }, [stats, runningExtraSeconds]);

  const personalWeek = useMemo(() => {
    if (!stats) return 0;
    return stats.personal.rangeSeconds + runningExtraSeconds;
  }, [stats, runningExtraSeconds]);

  const ownedExtra = runningOnOwnedTask ? runningExtraSeconds : 0;
  const ownedToday = (stats?.owned.todaySeconds || 0) + ownedExtra;
  const ownedWeek = (stats?.owned.rangeSeconds || 0) + ownedExtra;

  const maxBar = useMemo(() => {
    if (!stats) return 1;
    return Math.max(1, ...stats.personal.days.map(d => d.seconds));
  }, [stats]);

  if (!stats) {
    return (
      <div className="rounded-lg border border-border bg-panel px-3 py-2 text-xs text-muted">
        Loading stats…
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-panel overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left hover:bg-panel2 transition-colors"
      >
        <div className="grid grid-cols-2 divide-x divide-border">
          <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-muted mb-0.5">Today</div>
            <div className="font-mono font-semibold text-base text-running">{formatDuration(personalToday)}</div>
            <div className="text-[10px] text-muted mt-0.5 font-mono">
              On my tasks: {formatDurationShort(ownedToday)}
            </div>
          </div>
          <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-muted mb-0.5">Last 7 days</div>
            <div className="font-mono font-semibold text-base text-text/90">{formatDuration(personalWeek)}</div>
            <div className="text-[10px] text-muted mt-0.5 font-mono">
              On my tasks: {formatDurationShort(ownedWeek)}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center py-1 border-t border-border bg-panel2/40">
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            className={`text-muted transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </button>

      {open && (
        <div className="border-t border-border bg-panel2/40">
          <div className="flex items-center gap-1 px-3 pt-2.5">
            <button
              onClick={e => { e.stopPropagation(); setTab('chart'); }}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded transition-colors ${
                tab === 'chart' ? 'bg-panel text-text border border-border' : 'text-muted hover:text-text'
              }`}
            >
              Chart
            </button>
            <button
              onClick={e => { e.stopPropagation(); setTab('week'); }}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded transition-colors ${
                tab === 'week' ? 'bg-panel text-text border border-border' : 'text-muted hover:text-text'
              }`}
            >
              Week
            </button>
          </div>

          {tab === 'chart' && (
            <div className="px-4 py-3">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-wider text-muted">My tracked time</div>
                <div className="text-[10px] text-muted font-mono">
                  max: {formatDurationShort(Math.max(maxBar, personalToday))}
                </div>
              </div>
              <div className="flex items-end gap-1.5 h-24 mb-1">
                {stats.personal.days.map((d, i) => {
                  const isToday = i === stats.personal.days.length - 1;
                  const seconds = isToday ? personalToday : d.seconds;
                  const scaleMax = Math.max(maxBar, personalToday, 60);
                  const heightPct = seconds > 0 ? Math.max(8, Math.round((seconds / scaleMax) * 100)) : 0;
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1.5 group h-full">
                      <div className={`text-[9px] font-mono leading-none ${isToday ? 'text-accent font-semibold' : 'text-muted opacity-0 group-hover:opacity-100'} transition-opacity`}>
                        {seconds > 0 ? formatDurationShort(seconds) : ''}
                      </div>
                      <div
                        className={`w-full rounded-t transition-colors ${isToday ? 'bg-accent shadow-[0_0_12px_rgba(91,141,239,0.4)]' : 'bg-border group-hover:bg-muted'}`}
                        style={{ height: seconds > 0 ? `${heightPct}%` : '2px', minHeight: seconds > 0 ? '8px' : '2px' }}
                      />
                      <div className={`text-[10px] ${isToday ? 'text-accent font-semibold' : 'text-muted'}`}>
                        {shortDay(d.date)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 pt-3 border-t border-border/60 text-[10px] text-muted leading-relaxed">
                <span className="text-text/80">Today / 7-day:</span> time you personally tracked.<br/>
                <span className="text-text/80">On my tasks:</span> total time on tasks you own (matches Airtable view).
              </div>
            </div>
          )}

          {tab === 'week' && (
            <div className="px-3 py-3 max-h-80 overflow-y-auto">
              {stats.personal.days.slice().reverse().map(d => {
                const isToday = d.date === stats.personal.days[stats.personal.days.length - 1].date;
                const dayTasks = (stats.weekByDay && stats.weekByDay[d.date]) || [];
                const dayTotal = dayTasks.reduce((s, t) => s + t.seconds, 0);
                if (!isToday && dayTasks.length === 0) return null;
                return (
                  <div key={d.date} className="mb-3 last:mb-0">
                    <div className="flex items-center justify-between px-2 py-1">
                      <div className={`text-xs font-medium ${isToday ? 'text-accent' : 'text-text/80'}`}>
                        {isToday ? 'Today' : shortDay(d.date)} · {d.date.slice(5)}
                      </div>
                      <div className="text-[10px] font-mono text-muted">
                        {formatDurationShort(dayTotal)}
                      </div>
                    </div>
                    {dayTasks.length === 0 ? (
                      <div className="px-2 pb-1 text-[11px] text-muted italic">No work logged</div>
                    ) : (
                      <div className="rounded-md border border-border bg-panel divide-y divide-border">
                        {dayTasks.map(t => (
                          <button
                            key={`${d.date}-${t.taskId}`}
                            onClick={e => { e.stopPropagation(); onTaskOpen?.(t.taskId); }}
                            className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-panel2 transition-colors text-left"
                          >
                            <span className="text-xs truncate flex-1 min-w-0">{t.name}</span>
                            <span className="text-[10px] font-mono text-muted ml-2">
                              {formatDurationShort(t.seconds)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {stats.personal.days.every(d => !(stats.weekByDay && stats.weekByDay[d.date]?.length)) && (
                <div className="text-center text-xs text-muted py-6">
                  No work logged in the past 7 days yet.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
